import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  RadioGroup,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Field } from "formik";

// Row-level components that each mount their own hook calls at the top level,
// which is what fixes the Rules of Hooks violation that existed when these
// hooks were called inside renderCurrentVaccinationDose() and
// renderPreviousVaccinationDose() - plain functions, not React components.
import VaccineCurrentDoseRow from "./VaccineCurrentDoseRow";
import VaccinePreviousDoseRow from "./VaccinePreviousDoseRow";

// Import pure business logic functions to evaluate single-dose administered status
import { getPWCurrentDoseStatus } from "./scenarios/pregnant-women-current";
import { getChildCurrentDoseStatus } from "./scenarios/children-current";
import { getPWPreviousDoseStatus } from "./scenarios/pregnant-women-previous";
import { getChildPreviousDoseStatus } from "./scenarios/children-previous";
import {
  createVaccineIdentityHelpers,
  createVaccineIdentityRegistry,
  createVaccineIdentityRegistryFromVaccines,
} from "./vaccinationUtils";
/**
 * VaccinationTabs - PHASE 5: UI Integration
 *
 * =========================================================================
 * ARCHITECTURE CHANGE: From render functions to child components
 * =========================================================================
 *
 * BEFORE (broken):
 *   renderCurrentVaccinationDose() called usePWCurrentLogicEnhanced() and
 *   useChildCurrentLogicEnhanced() inside an if/else block. This is a direct
 *   violation of the Rules of Hooks because:
 *     1. The function is called inside .map(), not at the top level of a component.
 *     2. The hooks were inside conditionals (if beneficiaryType === 'PW').
 *   ESLint was suppressed with eslint-disable-next-line comments, which masked
 *   the real risk: React can silently misassign hook state across renders when
 *   the call order changes.
 *
 * AFTER (correct):
 *   VaccineCurrentDoseRow and VaccinePreviousDoseRow are real React components.
 *   Each instance mounts independently. Both PW and CHILD hooks are called
 *   unconditionally at the top of each row component, and the correct result
 *   is selected with a plain variable assignment. This is the standard React
 *   pattern for conditionally using one of two hooks.
 *
 * This component is now a pure coordinator: it manages tabs, the select-all
 * header, and the vaccine-level checkboxes. All dose-level business logic lives
 * in the row components and the scenario hooks they consume.
 */
const VaccinationTabs = ({
  // Tab management
  currentTab,
  onTabChange,

  // Context configuration
  allowedTabs = ['current', 'previous'], // Controls which tabs to render
  isAfterEdd = false,

  // Form data
  values,
  setFieldValue,

  // Vaccination data
  currentVaccinations,
  previousVaccinations,

  // Loading state
  isDataLoaded,

  // Handlers for vaccine selection
  onSelectAllToggle,
  onVaccineToggle,
  onDoseSelection,
  onPreviousDoseToggle,
  onPreviousDoseDate,

  // Generic beneficiary data consumed by row-level components
  beneficiaryType,         // 'PW' | 'CHILD'
  profileData,             // { lmc_date, vaccination } for PW | { dob } for CHILD
  administeredVaccinations,
  decryptFunction,
  identityContext = null,
  vaccineIdentity: vaccineIdentityProp = null,
  isCurrentFailed,
  isPrevFailed,
}) => {
  /**
   * Build the tab configuration based on allowed tabs.
   * Maps tab types to their display labels and indices.
   */
  const tabConfig = allowedTabs.map((tabType, index) => ({
    type: tabType,
    label: tabType === 'current' ? 'Current Vaccination' : 'Previous Vaccination',
    index
  }));

  /**
   * Get the current tab type based on the current tab index.
   */
  const getCurrentTabType = () => {
    return tabConfig[currentTab]?.type || 'current';
  };

  /**
   * Check if a specific tab type is the currently active one.
   */
  const isCurrentTabType = (tabType) => {
    return getCurrentTabType() === tabType;
  };
  const currentTabType = tabConfig[currentTab]?.type || 'current';

  const activeTabVaccines = useMemo(() => {
    if (currentTabType === 'current' && isAfterEdd) {
      return [];
    }
    return currentTabType === 'current'
      ? values.current_vaccines || []
      : values.previous_vaccines || [];
  }, [currentTabType, isAfterEdd, values.current_vaccines, values.previous_vaccines]);

  /**
   * Return whichever vaccine list corresponds to the active tab.
   * Using values.current_vaccines / values.previous_vaccines (Formik state)
   * rather than the prop arrays ensures the UI reflects in-flight user changes.
   */
  const getCurrentTabVaccines = () => {
    const tabType = getCurrentTabType();
    if (tabType === 'current' && isAfterEdd) {
      return [];
    }
    return tabType === 'current'
      ? values.current_vaccines || []
      : values.previous_vaccines || [];
  };

  const vaccineIdentity = useMemo(() => {
    if (vaccineIdentityProp) {
      return vaccineIdentityProp;
    }

    if (identityContext?.registry) {
      return createVaccineIdentityHelpers(
        identityContext.registry,
        decryptFunction,
        identityContext.matchOptions || {}
      );
    }

    const fallbackRegistry = createVaccineIdentityRegistryFromVaccines(
      [
        ...(values.current_vaccines || []),
        ...(values.previous_vaccines || []),
        ...(currentVaccinations || []),
        ...(previousVaccinations || []),
      ],
      decryptFunction
    );

    if (fallbackRegistry.byId.size > 0) {
      return createVaccineIdentityHelpers(fallbackRegistry, decryptFunction);
    }

    return createVaccineIdentityHelpers(
      createVaccineIdentityRegistry([], decryptFunction),
      decryptFunction
    );
  }, [
    vaccineIdentityProp,
    identityContext,
    decryptFunction,
    values.current_vaccines,
    values.previous_vaccines,
    currentVaccinations,
    previousVaccinations,
  ]);

  const sessionConflictState = useMemo(() => {
    const currentVaccines = values.current_vaccines || [];
    const previousVaccines = values.previous_vaccines || [];
    const activeTabVaccines =
      currentTabType === 'current' ? currentVaccines : previousVaccines;

    // Mutual-exclusion rules apply only within the active tab (one tab submitted per action).
    const isPentavalentSelected = activeTabVaccines.some(
      (v) => vaccineIdentity.isPentavalent(v) && v.selected
    );
    const isDptPrimarySelected = activeTabVaccines.some(
      (v) => vaccineIdentity.isDptPrimary(v) && v.selected
    );

    const adminMrCount = (administeredVaccinations || []).filter((r) =>
      vaccineIdentity.isMr(r)
    ).length;
    const isMmrAdministered = (administeredVaccinations || []).some((r) =>
      vaccineIdentity.isMmr(r)
    );

    const isMmrSelected = activeTabVaccines.some(
      (v) => vaccineIdentity.isMmr(v) && v.selected
    );

    const isMr2Selected = activeTabVaccines.some((v) => {
      if (!vaccineIdentity.isMr(v) || !v.selected) {
        return false;
      }
      if (currentTabType === 'current') {
        return v.selectedDose === 'MR-2';
      }
      return v.availableDoses?.[1]?.selected === true;
    });

    const disableMr2 = isMmrSelected || isMmrAdministered;
    const disableMmr = isMr2Selected || adminMrCount >= 2;

    const adminDptCount = (administeredVaccinations || []).filter((r) =>
      vaccineIdentity.isDptPrimary(r)
    ).length;
    const activeDptObj = activeTabVaccines.find((v) =>
      vaccineIdentity.isDptPrimary(v)
    );
    const activeDptCount =
      currentTabType === 'current'
        ? activeDptObj?.selectedDose
          ? 1
          : 0
        : activeDptObj?.availableDoses
          ? activeDptObj.availableDoses.filter((d) => d.selected).length
          : 0;
    const totalDptDoses = adminDptCount + activeDptCount;
    const isDptBoosterForceDisabled = totalDptDoses > 0 && totalDptDoses < 3;

    return {
      isPentavalentSelected,
      isDptPrimarySelected,
      disableMr2,
      disableMmr,
      isDptBoosterForceDisabled,
    };
  }, [
    currentTabType,
    values.current_vaccines,
    values.previous_vaccines,
    administeredVaccinations,
    vaccineIdentity,
  ]);

  return (
    <Box>
      {/* ================= VACCINATION TABS ================= */}
      {tabConfig.length > 1 && (
        <Tabs
          value={currentTab}
          onChange={onTabChange}
          sx={{
            mt: 2,
            "& .MuiTab-root": {
              fontFamily: "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              textTransform: "none",
              color: "#374151",
            },
            "& .Mui-selected": {
              color: "#1976d2",
              fontWeight: 600,
            },
          }}
        >
          {tabConfig.map((tab) => (
            <Tab key={tab.type} label={tab.label} />
          ))}
        </Tabs>
      )}

      {/* ================= VACCINATION CONTENT ================= */}
      {isDataLoaded && (
        <Box
          sx={{
            mt: 2,
            background: "#f8fffe",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {getCurrentTabVaccines().length > 0 && (
            <>
              {/* ====== SELECT ALL HEADER (Current tab only) ====== */}
              {isCurrentTabType('current') && (
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Field name="select_all_vaccines">
                    {({ field }) => (
                      <Checkbox
                        checked={values.select_all_vaccines}
                        onChange={(e) => onSelectAllToggle(e.target.checked)}
                        sx={{
                          color: "white",
                          "&.Mui-checked": { color: "white" },
                        }}
                      />
                    )}
                  </Field>
                  <Typography
                    fontSize={14}
                    sx={{
                      color: "white",
                      fontFamily:
                        "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Select all doses for which eligible
                  </Typography>
                </Box>
              )}

              {/* ====== INDIVIDUAL VACCINES ====== */}
              <Box sx={{ p: 2 }}>
                {activeTabVaccines.map((vaccine, index) => {
                  const {
                    isPentavalentSelected,
                    isDptPrimarySelected,
                    disableMr2,
                    disableMmr,
                    isDptBoosterForceDisabled,
                  } = sessionConflictState;

                  // Evaluate if it's a single-dose vaccine that's already administered
                  let isSingleDoseAdministered = false;
                  let singleDoseDate = "";

                  if (vaccine.availableDoses?.length === 1) {
                    const isCurrent = isCurrentTabType('current');
                    const selectedDosesSnapshot =
                      vaccine.availableDoses?.map((dose) => ({
                        selected: dose.selected || false,
                        administeredDate: dose.administeredDate || null,
                      })) || [];
                    let doseConfig;
                    if (isCurrent) {
                      doseConfig = beneficiaryType === "PW"
                        ? getPWCurrentDoseStatus(vaccine, 0, profileData, decryptFunction, identityContext)
                        : getChildCurrentDoseStatus(
                            vaccine,
                            0,
                            profileData?.dob,
                            administeredVaccinations,
                            decryptFunction,
                            identityContext
                          );
                    } else {
                      doseConfig = beneficiaryType === "PW"
                        ? getPWPreviousDoseStatus(
                            vaccine,
                            0,
                            profileData,
                            decryptFunction,
                            selectedDosesSnapshot,
                            identityContext
                          )
                        : getChildPreviousDoseStatus(
                            vaccine,
                            0,
                            profileData?.dob,
                            administeredVaccinations,
                            decryptFunction,
                            selectedDosesSnapshot,
                            values.previous_vaccines,
                            identityContext
                          );
                    }
                    if (doseConfig?.status === "administered") {
                      isSingleDoseAdministered = true;
                      singleDoseDate = doseConfig.date || "";
                    }
                  }

                    const isPentavalent = vaccineIdentity.isPentavalent(vaccine);
                    // Exact match — DPT-Booster must NOT be treated as DPT primary
                    const isDptPrimary  = vaccineIdentity.isDptPrimary(vaccine);
                    const isDptBooster  = vaccineIdentity.isDptBooster(vaccine);
                    const isMr  = vaccineIdentity.isMr(vaccine);
                    const isMmr = vaccineIdentity.isMmr(vaccine);

                    const isCheckboxDisabled =
                      // Pentavalent disabled when DPT primary is selected in session
                      (isPentavalent && isDptPrimarySelected) ||
                      // DPT primary disabled when Pentavalent is selected in session
                      (isDptPrimary && isPentavalentSelected) ||
                      // DPT Booster disabled when total DPT doses < 3
                      (isDptBooster && isDptBoosterForceDisabled) ||
                      // MMR disabled when MR-2 is selected in this session
                      (isMmr && disableMmr);

                  return (
                    <Box key={vaccine.id}>
                      {isSingleDoseAdministered ? (
                        // Render simple text for fully administered single-dose vaccines
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ py: 1.25, pl: "10px", pr: 1 }}
                        >
                          <Typography
                            fontSize={14}
                            color="text.disabled"
                            sx={{
                              fontFamily:
                                "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                              fontWeight: 500,
                            }}
                          >
                            {vaccine.name}
                          </Typography>
                          <Typography
                            fontSize={12}
                            color="text.primary"
                            sx={{ fontWeight: 500 }}
                          >
                            {singleDoseDate}
                          </Typography>
                        </Box>
                      ) : (
                        // Standard interactive rendering
                        <>
                          {/* Vaccine-level checkbox and name */}
                          <Box display="flex" alignItems="center" gap={1}>
                            <Checkbox
                              checked={vaccine.selected}
                              disabled={isCheckboxDisabled}
                              onChange={(e) =>
                                onVaccineToggle(vaccine.id, e.target.checked)
                              }
                              sx={{ "&.Mui-checked": { color: "#1976d2" } }}
                            />
                            <Typography
                              fontSize={14}
                              sx={{
                                fontFamily:
                                  "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                                fontWeight: 500,
                                color: isCheckboxDisabled ? "text.disabled" : "#1f2937",
                              }}
                            >
                              {vaccine.name}
                            </Typography>
                          </Box>

                          {/* Dose rows - only rendered when the vaccine is selected */}
                          {vaccine.selected && (
                            <>
                              <Divider sx={{ my: 1 }} />

                              <Box sx={{ pl: 4, pb: 1 }}>
                                {/* ---- CURRENT TAB: radio buttons via VaccineCurrentDoseRow ---- */}
                                {isCurrentTabType('current') && (
                                  <RadioGroup
                                    value={vaccine.selectedDose || ""}
                                    onChange={(e) =>
                                      onDoseSelection(vaccine.id, e.target.value)
                                    }
                                  >
                                    {vaccine.availableDoses?.map((dose, doseIndex) => (
                                      <VaccineCurrentDoseRow
                                        key={`${vaccine.id}-current-${doseIndex}`}
                                        vaccine={vaccine}
                                        dose={dose}
                                        doseIndex={doseIndex}
                                        beneficiaryType={beneficiaryType}
                                        profileData={profileData}
                                        administeredVaccinations={administeredVaccinations}
                                        decryptFunction={decryptFunction}
                                        identityContext={identityContext}
                                        forceDisabled={
                                          (isMmr && doseIndex === 0 && disableMmr) ||
                                          (isMr && doseIndex === 1 && disableMr2) ||
                                          (isDptBooster && isDptBoosterForceDisabled)
                                        }
                                        forceDisabledReason={
                                          isMr && doseIndex === 1 && disableMr2
                                            ? ""
                                            : isMmr && doseIndex === 0 && disableMmr
                                            ? ""
                                            : isDptBooster && isDptBoosterForceDisabled
                                            ? ""
                                            : ""
                                        }
                                      />
                                    ))}
                                  </RadioGroup>
                                )}

                                {/* ---- PREVIOUS TAB: checkboxes via VaccinePreviousDoseRow ---- */}
                                {isCurrentTabType('previous') && (
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                      }}
                                    >
                                      {vaccine.availableDoses?.map((dose, doseIndex) => (
                                        <VaccinePreviousDoseRow
                                          key={`${vaccine.id}-previous-${doseIndex}`}
                                          vaccine={vaccine}
                                          dose={dose}
                                          doseIndex={doseIndex}
                                          beneficiaryType={beneficiaryType}
                                          profileData={profileData}
                                          administeredVaccinations={administeredVaccinations}
                                          decryptFunction={decryptFunction}
                                          identityContext={identityContext}
                                          previousVaccines={values.previous_vaccines}
                                          onDoseToggle={(idx, checked) =>
                                            onPreviousDoseToggle(vaccine.id, idx, checked)
                                          }
                                          onDoseDate={(idx, date) =>
                                            onPreviousDoseDate(vaccine.id, idx, date)
                                          }
                                          forceDisabled={
                                            (isMmr && doseIndex === 0 && disableMmr) ||
                                            (isMr && doseIndex === 1 && disableMr2) ||
                                            (isDptBooster && isDptBoosterForceDisabled)
                                          }
                                          forceDisabledReason={
                                            isMr && doseIndex === 1 && disableMr2
                                              ? ""
                                              : isMmr && doseIndex === 0 && disableMmr
                                              ? ""
                                              : isDptBooster && isDptBoosterForceDisabled
                                              ? ""
                                              : ""
                                          }
                                        />
                                      ))}
                                    </Box>
                                  </LocalizationProvider>
                                )}
                              </Box>
                            </>
                          )}
                        </>
                      )}

                      {/* Divider between vaccines (not after the last one) */}
                      {index < activeTabVaccines.length - 1 && (
                        <Divider sx={{ my: 1.5 }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* ====== EMPTY STATE ====== */}
          {activeTabVaccines.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                <img src="/Uwin/no_vaccine.png" alt="No Vaccine Available" style={{ maxWidth: '180px', height: 'auto', marginBottom: '16px' }} />
                <Typography variant="h6" sx={{ color: '#111827', fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif', mb: 1 }}>
                  {isCurrentTabType('current')
                    ? "Vaccines are not available for this member"
                    : "Previous Vaccines are not available for this member"}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ====== LOADING STATE ====== */}
      {!isDataLoaded && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography
            fontSize={14}
            color="text.secondary"
            sx={{
              mt: 1,
              fontFamily:
                "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            Loading vaccination data...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VaccinationTabs;
